"use client";

import {
  DISPATCH_ACTION,
  PayPalScriptProvider,
  SCRIPT_LOADING_STATE,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { useNewCartStore } from "@/stores/useNewCartStore";

const options = {
  clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID ?? "",
  components: "messages,buttons",
  currency: "USD",
  intent: "capture",
};

interface Props {
  children: React.ReactNode;
}

/**
 * Loads the PayPal JS SDK on demand: the first time the cart opens or a checkout
 * route renders. Consumers (cart PayPalMessages, checkout PayPalButtons) render
 * their pending state until the script resolves.
 *
 * The provider itself must stay mounted at the ROOT layout for the app's lifetime.
 * Do not move it into the cart sheet or a route-group layout: PayPal's zoid iframe
 * framework throws "zoid destroyed all components" whenever a mounted provider
 * unmounts (sheet close, shop → checkout navigation). See .claude/failures.md —
 * this has shipped crashes twice. deferLoading gives the perf win instead: no SDK
 * bytes on any page until a PayPal surface can actually appear.
 */
function PayPalScriptLoader() {
  const [{ isInitial }, dispatch] = usePayPalScriptReducer();
  const isCartOpen = useNewCartStore(state => state.isCartOpen);
  const pathname = usePathname();
  const shouldLoad = isCartOpen || pathname.startsWith("/store/checkout");

  useEffect(() => {
    if (isInitial && shouldLoad) {
      dispatch({ type: DISPATCH_ACTION.LOADING_STATUS, value: SCRIPT_LOADING_STATE.PENDING });
    }
  }, [isInitial, shouldLoad, dispatch]);

  return null;
}

export function PayPalProvider({ children }: Props) {
  if (!options.clientId) return <>{children}</>;
  return (
    <PayPalScriptProvider options={options} deferLoading={true}>
      <PayPalScriptLoader />
      {children}
    </PayPalScriptProvider>
  );
}
